import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import { formatMoney } from '../cashbacks/formatMoney';
import type { Market } from '../dataProvider/types';
import type { EarningsBand as Band } from './earningsModel';
import { EarningsTable } from './EarningsTable';
import { EarningsTrend } from './EarningsTrend';

/**
 * One full-width market band: the reconciliation chain (the only place the
 * welcome bonus surfaces), the pending pipeline and volume beside it, the ranked
 * per-entity table, and the gated two-line trend. A band is wholly
 * self-contained and single-currency; there is deliberately NO cross-band total
 * anywhere (the page never sums £ and $ together).
 */

const MARKET_NAME: Record<Market, string> = {
  uk: 'United Kingdom',
  us: 'United States',
  ie: 'Ireland',
};

const Figure = ({
  label,
  value,
  testId,
  emphasis,
}: {
  label: string;
  value: string;
  testId: string;
  emphasis?: boolean;
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography
      variant={emphasis ? 'h5' : 'h6'}
      component="span"
      data-testid={testId}
      sx={{ fontWeight: emphasis ? 700 : 500 }}
    >
      {value}
    </Typography>
  </Box>
);

const Operator = ({ word }: { word: string }) => (
  <Typography
    variant="body2"
    color="text.secondary"
    sx={{ alignSelf: 'center' }}
  >
    {word}
  </Typography>
);

export const EarningsBand = ({ band }: { band: Band }) => {
  const m = formatMoney;
  return (
    <Card
      variant="outlined"
      data-testid={`earnings-band-${band.market}`}
      sx={{ mb: 3 }}
    >
      <CardContent>
        <Typography variant="h6" component="h2" sx={{ mb: 0.5 }}>
          {MARKET_NAME[band.market]}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {band.currency.toUpperCase()}
        </Typography>

        {/* Reconciliation chain + pipeline / volume beside it. */}
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'stretch',
            gap: 3,
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Figure
              label="Confirmed margin"
              value={m(band.confirmedMargin, band.currency)}
              testId={`recon-confirmed-${band.market}`}
            />
            <Operator word="minus" />
            <Figure
              label="Welcome bonus"
              value={m(band.welcomeBonus, band.currency)}
              testId={`recon-welcome-${band.market}`}
            />
            <Operator word="equals" />
            <Figure
              label="Contribution"
              value={m(band.contribution, band.currency)}
              testId={`recon-contribution-${band.market}`}
              emphasis
            />
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box sx={{ display: 'flex', gap: 3 }}>
            <Figure
              label="Pending pipeline"
              value={m(band.pendingMargin, band.currency)}
              testId={`recon-pending-${band.market}`}
            />
            <Box>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Volume
              </Typography>
              <Typography
                variant="h6"
                component="span"
                data-testid={`recon-volume-${band.market}`}
              >
                {band.volume}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                qualifying transactions
              </Typography>
            </Box>
          </Box>
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: 1 }}
        >
          Pending is shown as pipeline only and is never blended into confirmed
          margin or contribution.
        </Typography>

        <Box sx={{ mt: 3 }}>
          <EarningsTable
            ranked={band.ranked}
            lowVolume={band.lowVolume}
            medianMarginRate={band.medianMarginRate}
            currency={band.currency}
          />
        </Box>

        {band.trend && (
          <Box sx={{ mt: 3 }}>
            <EarningsTrend points={band.trend} currency={band.currency} />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
